import React, { useState, useEffect } from 'react';
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAppId } from "../context/AppIdContext";
import '../style/boothPramukh.css'
import Header from '../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothPramukh = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Mobile Search State ---
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

    // --- Create Pramukh Modal State ---
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        booth_no: '',
        name: '',
        mobile_no: ''
    });

    const { appId, language, userId } = useAppId();
    const navigate = useNavigate();

    const lang = language || 'en';

    const t = {
        en: {
            loadingData: "Loading Data...",
            title: "Booth Pramukh & Booth List",
            subtitle: "Manage platform administrators and their permissions.",
            addBtn: "Add Booth",
            searchPlaceholder: "Search by Booth Number...",
            boothNo: "Booth No.",
            totalPramukh: "Pramukh:",
            noPramukh: "No Pramukh Available",
            viewBtn: "View",
            createBtn: "Create Pramukh",
            noResultsPrefix: "No booth found matching",
            noDataAvailable: "No data available",
            modalTitle: "Create Booth Pramukh",
            nameLabel: "Full Name",
            mobileLabel: "Mobile Number",
            cancelBtn: "Cancel",
            submitBtn: "Create",
            submitting: "Creating..."
        },
        hi: {
            loadingData: "डेटा लोड हो रहा है...",
            title: "बूथ प्रमुख और बूथ सूची",
            subtitle: "प्लेटफ़ॉर्म प्रशासकों और उनकी अनुमतियों का प्रबंधन करें।",
            addBtn: "बूथ जोड़ें",
            searchPlaceholder: "बूथ नंबर द्वारा खोजें...",
            boothNo: "बूथ नं.",
            totalPramukh: "प्रमुख:",
            noPramukh: "कोई प्रमुख उपलब्ध नहीं",
            viewBtn: "देखें",
            createBtn: "प्रमुख बनाएं",
            noResultsPrefix: "इससे मेल खाने वाला कोई बूथ नहीं मिला",
            noDataAvailable: "कोई डेटा उपलब्ध नहीं है",
            modalTitle: "बूथ प्रमुख बनाएं",
            nameLabel: "पूरा नाम",
            mobileLabel: "मोबाइल नंबर",
            cancelBtn: "रद्द करें",
            submitBtn: "बनाएं",
            submitting: "बना रहा है..."
        },
        gu: {
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            title: "બૂથ પ્રમુખ અને બૂથ યાદી",
            subtitle: "પ્લેટફોર્મ સંચાલકો અને તેમની પરવાનગીઓનું સંચાલન કરો.",
            addBtn: "બૂથ ઉમેરો",
            searchPlaceholder: "બૂથ નંબર દ્વારા શોધો...",
            boothNo: "બૂથ નં.",
            totalPramukh: "પ્રમુખ:",
            noPramukh: "કોઈ પ્રમુખ ઉપલબ્ધ નથી",
            viewBtn: "જુઓ",
            createBtn: "પ્રમુખ બનાવો",
            noResultsPrefix: "કોઈ બૂથ મળ્યું નથી",
            noDataAvailable: "કોઈ ડેટા ઉપલબ્ધ નથી",
            modalTitle: "બૂથ પ્રમુખ બનાવો",
            nameLabel: "પૂરું નામ",
            mobileLabel: "મોબાઇલ નંબર",
            cancelBtn: "રદ કરો",
            submitBtn: "બનાવો",
            submitting: "બનાવી રહ્યા છીએ..."
        }
    };

    const txt = t[lang];

    useEffect(() => {
        if (appId) {
            fetchUsers();
        } else {
            navigate("/");
        }
    }, [appId]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_booth_pramukh_and_total_booth_list`,
                { app_id: appId }
            );

            if (response.data.success && response.data.data && response.data.data.result) {
                setUsers(response.data.data.result);
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.error("API Error:", err);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreateModal = (boothNo) => {
        setCreateFormData({
            booth_no: boothNo,
            name: '',
            mobile_no: ''
        });
        setIsCreateModalOpen(true);
    };

    const handleCreateInputChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.name || !createFormData.mobile_no) {
            alert("Please fill in all fields.");
            return;
        }

        if (!/^\d{10}$/.test(createFormData.mobile_no)) {
            alert("Please enter a valid 10-digit mobile number.");
            return;
        }

        setIsSubmitting(true);

        const payload = {
            app_id: appId,
            name: createFormData.name,
            mobile_no: createFormData.mobile_no,
            user_type: "BP",
            booth_no: createFormData.booth_no,
            booth_list: "",
            create_by: userId || "Admin"
        };

        try {
            const response = await axios.post(`${BASE_URL}/api/insert_user`, payload);
            if (response.data.success) {
                alert("Booth Pramukh created successfully!");
                setIsCreateModalOpen(false);
                setCreateFormData({ booth_no: '', name: '', mobile_no: '' });
                fetchUsers();
            } else {
                alert("Error: " + JSON.stringify(response.data));
            }
        } catch (error) {
            console.error("API Error:", error);
            alert("Failed to create Booth Pramukh.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredUsers = users.filter(user => {
        if (!searchTerm) return true;
        return user.booth_no?.toString().includes(searchTerm);
    });

    if (loading) {
        return (
            <div className="loader-wrapper">
                <div className="spinner"></div>
                <p className="loader-text">{txt.loadingData}</p>
                <style jsx>{`
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                    .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                `}</style>
            </div>
        );
    }

    return (
        <>
            {/* Create Pramukh Modal */}
            {isCreateModalOpen && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-box">
                        <div className="custom-modal-header">
                            <h2>{txt.modalTitle}</h2>
                            <button className="custom-close-btn" onClick={() => setIsCreateModalOpen(false)}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleCreateSubmit}>
                            <div className="custom-form-body">
                                <div className="custom-input-group">
                                    <label>{txt.boothNo}</label>
                                    <input type="text" value={createFormData.booth_no} disabled style={{ background: '#f3f4f6', cursor: 'not-allowed' }} />
                                </div>
                                <div className="custom-input-group">
                                    <label>{txt.nameLabel}</label>
                                    <input type="text" name="name" value={createFormData.name} onChange={handleCreateInputChange} required />
                                </div>
                                <div className="custom-input-group">
                                    <label>{txt.mobileLabel}</label>
                                    <input type="text" name="mobile_no" value={createFormData.mobile_no} onChange={handleCreateInputChange} maxLength="10" required />
                                </div>
                            </div>
                            <div className="custom-modal-footer">
                                <button type="button" className="btn-secondary" onClick={() => setIsCreateModalOpen(false)}>{txt.cancelBtn}</button>
                                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? txt.submitting : txt.submitBtn}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style jsx>{`
                /* MODAL RESPONSIVE FIXES */
                .custom-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15, 23, 42, 0.75); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 10000; padding: 20px; box-sizing: border-box; }
                .custom-modal-box { background: white; border-radius: 16px; width: 100%; max-width: 420px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); overflow: hidden; }
                .custom-modal-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .custom-modal-header h2 { margin: 0; font-size: 1.25rem; color: #0f172a; font-weight: 700; }
                .custom-close-btn { background: none; border: none; color: #94a3b8; cursor: pointer; }
                .custom-form-body { padding: 24px; display: flex; flex-direction: column; gap: 16px; }
                .custom-input-group { display: flex; flex-direction: column; gap: 6px; }
                .custom-input-group label { font-size: 13px; font-weight: 600; color: #475569; }
                .custom-input-group input { padding: 12px 16px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; outline: none; }
                .custom-modal-footer { padding: 16px 24px; background: #f8fafc; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 12px; }
                .btn-secondary { padding: 10px 16px; border: 1px solid #cbd5e1; background: white; color: #475569; border-radius: 8px; font-weight: 600; cursor: pointer; }
                .btn-primary { padding: 10px 20px; border: none; background: #2563eb; color: white; border-radius: 8px; font-weight: 600; cursor: pointer; }

                /* --- PROFESSIONAL FILTER CONTAINER --- */
                .filter-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 24px;
                }
                .action-group { display: flex; align-items: center; gap: 12px; }
                .mobile-search-trigger { display: none; }
                .close-search-btn { display: none; }

                .search-wrapper { position: relative; flex: 1; max-width: 400px; }
                .search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 20px; height: 20px; }
                .search-input { width: 100%; padding: 12px 16px 12px 42px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; color: #1e293b; outline: none; transition: all 0.2s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

                .btn-add-user { display: flex; align-items: center; justify-content: center; gap: 8px; background-color: #2563eb; color: white; padding: 12px 20px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2); white-space: nowrap; }

                /* User Grid - Desktop Default (4 columns) */
                .user-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 20px;
                }

                /* No Data Message Styling */
                .no-data-message {
                    grid-column: 1 / -1;
                    text-align: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }

                .no-data-icon {
                    font-size: 64px;
                    margin-bottom: 16px;
                    opacity: 0.5;
                }

                .no-data-text {
                    font-size: 16px;
                    font-weight: 500;
                    color: #6b7280;
                }

                @media (max-width: 640px) {
                    .filter-container { 
                        flex-direction: row; 
                        align-items: center; 
                        gap: 8px; 
                    }
                    
                    .search-wrapper { 
                        display: block; 
                        flex: 1; 
                        max-width: none; 
                    }

                    .search-input {
                        padding: 10px 16px 10px 36px;
                        font-size: 13px;
                    }

                    .search-icon {
                        left: 10px;
                        width: 16px;
                        height: 16px;
                    }

                    .close-search-btn { 
                        display: block; 
                        position: absolute; 
                        right: 8px; 
                        top: 50%; 
                        transform: translateY(-50%); 
                        background: #f1f5f9; 
                        border: none; 
                        color: #64748b; 
                        padding: 4px; 
                        border-radius: 50%; 
                        cursor: pointer; 
                        display: flex; 
                        align-items: center; 
                    }

                    .action-group { 
                        width: auto; 
                    }

                    .btn-add-user { 
                        width: auto; 
                        padding: 10px 12px;
                        font-size: 13px;
                        gap: 4px;
                        height: 46px;
                    }

                    .btn-add-user svg {
                        width: 16px;
                        height: 16px;
                    }

                    .mobile-search-trigger { 
                        display: none; 
                    }

                    /* Show at least 2 booths in mobile responsive */
                    .user-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 12px;
                    }

                    /* Adjust card padding for mobile */
                    .user-card {
                        padding: 16px 12px;
                    }

                    .booth-circle {
                        width: 60px;
                        height: 60px;
                        font-size: 20px;
                        margin: 8px auto;
                    }

                    .card-name {
                        font-size: 14px;
                        margin-top: 12px !important;
                    }

                    .btn-action {
                        padding: 8px 12px;
                        font-size: 13px;
                        margin-top: 12px;
                    }

                    .no-data-message {
                        padding: 40px 16px;
                    }

                    .no-data-icon {
                        font-size: 48px;
                    }

                    .no-data-text {
                        font-size: 14px;
                    }
                }
            `}</style>

            <div className={`app-container p-4 ${isCreateModalOpen ? 'blur-background' : ''}`}>

                {/* Modern Reusable Header */}
                <Header title={txt.title} />

                {/* Professional Action Bar */}
                <div className="filter-container">
                    <div className={`search-wrapper ${isMobileSearchOpen ? 'mobile-open' : ''}`}>
                        <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={txt.searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="action-group">
                        {!isMobileSearchOpen && (
                            <button className="mobile-search-trigger" onClick={() => setIsMobileSearchOpen(true)}>
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* User Grid */}
                <div className="user-grid">
                    {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                            <div key={user.id} className="user-card">
                                <div className="menu-dots">
                                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 13a1 1 0 100-2 1 1 0 000 2zM12 7a1 1 0 100-2 1 1 0 000 2zM12 19a1 1 0 100-2 1 1 0 000 2z" />
                                    </svg>
                                </div>

                                <h5 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                                    {txt.boothNo}
                                </h5>

                                <div className="booth-circle">
                                    {user.booth_no}
                                </div>

                                <h5 className="card-name text-primary" style={{ marginTop: '16px', fontSize: '16px' }}>
                                    {user.total_booth_pramukh ? (
                                        <>{txt.totalPramukh} <span style={{ color: 'var(--color-brand)' }}>{user.total_booth_pramukh}</span></>
                                    ) : (
                                        <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>{txt.noPramukh}</span>
                                    )}
                                </h5>

                                {user.total_booth_pramukh > 0 ? (
                                    <Link to='/booth_pramukh_detail_with_voters' state={{ selectedBoothNo: user.booth_no }} className='btn-action btn-green text-center text-decoration-none'>
                                        {txt.viewBtn}
                                    </Link>
                                ) : (
                                    <button
                                        className="btn-action btn-red"
                                        onClick={() => handleOpenCreateModal(user.booth_no)}
                                    >
                                        {txt.createBtn}
                                    </button>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="no-data-message">
                            <div className="no-data-icon">📭</div>
                            <div className="no-data-text">
                                {searchTerm ? `${txt.noResultsPrefix} "${searchTerm}"` : txt.noDataAvailable}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BoothPramukh;